import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import PDFPreviewDialog from './PDFPreviewDialog';
import PDFPreviewDialogStructured from './PDFPreviewDialogStructured';
import { CircularProgress, Box, Alert, Dialog } from '@mui/material';

/**
 * Separate Seite für PDF-Vorschau in eigenem Fenster
 * Wird über window.open() von Search.js aufgerufen
 */
const PDFPreviewWindow = () => {
  const [searchParams] = useSearchParams();
  const [pdfData, setPdfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [templateType, setTemplateType] = useState('F'); // Template-Typ State
  
  // Entferne body padding/margin für das Popup-Fenster
  useEffect(() => {
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.overflow = '';
    };
  }, []);
  
  useEffect(() => {
    console.log('🔍 PDFPreviewWindow useEffect gestartet');
    const key = searchParams.get('key');
    console.log('🔍 LocalStorage Key:', key);
    
    // ✅ TEMPLATE-TYP AUS KEY EXTRAHIEREN
    // Key-Format: pdf_preview_${reportId}_${templateType}_${timestamp}
    let extractedTemplateType = 'F'; // Default: Freitext
    if (key) {
      const keyParts = key.split('_');
      console.log('🔍 Key Parts:', keyParts);
      
      // Suche nach Template-Typ in Key (S oder F)
      const templateIndex = keyParts.findIndex(part => part === 'S' || part === 'F');
      if (templateIndex !== -1) {
        extractedTemplateType = keyParts[templateIndex];
        console.log('🔍 ✅ Template-Typ aus Key extrahiert:', extractedTemplateType);
      } else {
        console.log('🔍 ⚠️ Kein Template-Typ im Key gefunden, verwende Fallback: F');
      }
    }
    
    setTemplateType(extractedTemplateType);
    console.log('🔍 Template-Typ gesetzt:', extractedTemplateType);
    
    // Wenn bereits geladen, überspringe den Rest
    if (pdfData) {
      console.log('✅ PDF-Daten bereits geladen, überspringe useEffect');
      return;
    }
    
    if (!key) {
      console.error('❌ Kein Schlüssel für PDF-Daten gefunden');
      setError('Kein Schlüssel für PDF-Daten gefunden');
      setLoading(false);
      return;
    }
    
    try {
      // Lade PDF-Daten aus localStorage
      const dataStr = localStorage.getItem(key);
      console.log('🔍 localStorage.getItem result:', dataStr ? 'Daten gefunden' : 'Keine Daten');
      
      if (!dataStr) {
        console.error('❌ PDF-Daten nicht gefunden oder abgelaufen');
        setError('PDF-Daten nicht gefunden oder abgelaufen');
        setLoading(false);
        return;
      }
      
      const data = JSON.parse(dataStr);
      console.log('✅ PDF-Daten erfolgreich geladen:', data);
      setPdfData(data);
      setLoading(false);
      
      // Entferne Daten aus localStorage nach dem Laden
      localStorage.removeItem(key);
      console.log('🗑️ PDF-Daten aus localStorage entfernt');
      
    } catch (err) {
      console.error('❌ Fehler beim Laden der PDF-Daten:', err);
      setError('Fehler beim Laden der PDF-Daten: ' + err.message);
      setLoading(false);
    }
  }, [searchParams, pdfData]);
  
  console.log('🔍 PDFPreviewWindow Render - loading:', loading, 'error:', error, 'pdfData:', !!pdfData);
  
  // Wenn pdfData vorhanden ist, ignoriere Error (kann von zweitem useEffect-Durchlauf stammen)
  if (pdfData) {
    console.log('✅ Rendere PDF Preview Dialog mit Template-Typ:', templateType);
    
    // Extrahiere highlightTerms aus pdfData
    const highlightTerms = pdfData.highlightTerms || [];
    console.log('🔍🔍🔍 Extracted highlightTerms from pdfData:', highlightTerms);
    
    // ✅ TEMPLATE-TYP-BEWUSSTE DIALOG-AUSWAHL
    if (templateType === 'S') {
      console.log('📋 Verwende PDFPreviewDialogStructured für strukturierten Befund');
      return (
        <PDFPreviewDialogStructured
          open={true}
          onClose={() => window.close()}
          pdfData={pdfData}
          reportTitle="Strukturierter Befund-Vorschau"
          isPopupWindow={true}
          highlightTerms={highlightTerms}
        />
      );
    } else {
      console.log('📝 Verwende PDFPreviewDialog für Freitext-Befund');
      return (
        <PDFPreviewDialog
          open={true}
          onClose={() => window.close()}
          pdfData={pdfData}
          reportTitle="Befund-Vorschau"
          isPopupWindow={true}
          highlightTerms={highlightTerms}
        />
      );
    }
  }
  
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        width: '100vw',
        backgroundColor: 'var(--rd-bg-subtle)'
      }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        p: 3 
      }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  
  // Fallback: sollte nie erreicht werden
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      p: 3 
    }}>
      <Alert severity="warning">Keine PDF-Daten verfügbar</Alert>
    </Box>
  );
};

export default PDFPreviewWindow;
